import type { PDFDocument } from "../types/PDFDocument"

// Render a specific page to a complete page object
export const RenderPage = async (
  { pdfium, doc_pointer }: PDFDocument,
  page: number,
  {
    scale = 2,
    max_size = 1980,
  }: {
    scale?: number
    max_size?: number
  } = {}
) => {
  // Load the page (convert 1-based page_index to 0-based for pdfium)
  const page_pointer = pdfium.FPDF_LoadPage(doc_pointer, page - 1)
  if (!page_pointer) {
    throw new Error(`Failed to load page ${page}`)
  }

  try {
    // Get the page dimensions
    const width = pdfium.FPDF_GetPageWidthF(page_pointer)
    const height = pdfium.FPDF_GetPageHeightF(page_pointer)

    // Calculate the scaled dimensions
    let scaled_width = Math.floor(width * scale)
    let scaled_height = Math.floor(height * scale)

    // Apply max_size constraint if specified
    if (max_size) {
      const max_dimension = Math.max(scaled_width, scaled_height)
      if (max_dimension > max_size) {
        const scale_factor = max_size / max_dimension
        scaled_width = Math.floor(scaled_width * scale_factor)
        scaled_height = Math.floor(scaled_height * scale_factor)
      }
    }

    // Create a bitmap for rendering with alpha channel
    const bitmap_pointer = pdfium.FPDFBitmap_Create(
      scaled_width,
      scaled_height,
      1 // Enable alpha channel for transparency
    )
    if (!bitmap_pointer) {
      throw new Error("Failed to create bitmap")
    }

    // Fill the bitmap with transparent background
    pdfium.FPDFBitmap_FillRect(
      bitmap_pointer,
      0,
      0,
      scaled_width,
      scaled_height,
      0x00000000 // Transparent background
    )

    // Render the page to the bitmap
    pdfium.FPDF_RenderPageBitmap(
      bitmap_pointer,
      page_pointer,
      0,
      0,
      scaled_width,
      scaled_height,
      0,
      16 // Use FPDF_REVERSE_BYTE_ORDER flag for correct color representation
    )

    // Get the bitmap buffer
    const bufferPtr = pdfium.FPDFBitmap_GetBuffer(bitmap_pointer)
    if (!bufferPtr) {
      throw new Error("Failed to get bitmap buffer")
    }

    const buffer_size = scaled_width * scaled_height * 4 // RGBA

    // Create a COPY of the buffer data to prevent memory issues
    const buffer = new Uint8Array(
      pdfium.pdfium.HEAPU8.buffer,
      pdfium.pdfium.HEAPU8.byteOffset + bufferPtr,
      buffer_size
    ).slice()

    pdfium.FPDFBitmap_Destroy(bitmap_pointer)

    // Create ImageData from the buffer copy
    const image_data = new ImageData(
      new Uint8ClampedArray(buffer.buffer),
      scaled_width,
      scaled_height
    )

    // Convert image data to blob URL
    const canvas = new OffscreenCanvas(scaled_width, scaled_height)
    const ctx = canvas.getContext("2d")
    ctx.putImageData(image_data, 0, 0)

    // Create the complete page object
    // Create the complete page object
    return {
      size: [0, 0, scaled_width, scaled_height],
      blob: await canvas.convertToBlob({ type: "image/png" }),
    }
  } finally {
    // Clean up page
    pdfium.FPDF_ClosePage(page_pointer)
  }
}
